import { ActiveAppQueries } from '@/app/platform/platform-core/user/queries/types';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { combineLatest, defer, EMPTY, merge, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import { UserNavigationItem } from '@/app/platform/platform-core/user/models/user-navigation-item';
import { navigationItemIdQueryParam } from '@/app/platform/platform-core/user/constants';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';

@Injectable({
  providedIn: 'root'
})
export class RouterActiveAppQueries implements ActiveAppQueries {
  constructor(
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries,
    private readonly router: Router
  ) {}

  /**
   * Diepste route na elke voltooide navigatie (vroeger Akita's router-store). Een late
   * abonnee krijgt meteen de huidige stand, als er al een navigatie voltooid is; daarvoor
   * emitteert er niets, net als bij de router-store.
   */
  private readonly committedLeaf$: Observable<ActivatedRouteSnapshot> = merge(
    defer(() => (this.router.navigated ? of(undefined) : EMPTY)),
    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
  ).pipe(map(() => this.leafSnapshot()));

  readonly activeApp$: Observable<UserEgiApp | undefined> = combineLatest([
    this.userQueries.availableApps$,
    this.selectParam('egiApp')
  ]).pipe(
    map(([availableApps, activeAppId]) => {
      return this.findActiveApp(availableApps, activeAppId);
    })
  );

  getActiveApp(): UserEgiApp | undefined {
    const apps = this.userQueries.getAvailableApps();
    const activeAppId = this.leafSnapshot().params['egiApp'];
    return this.findActiveApp(apps, activeAppId);
  }

  readonly activeNavigationItem$: Observable<UserNavigationItem | undefined> = combineLatest([
    this.activeApp$,
    this.selectQueryParam(navigationItemIdQueryParam)
  ]).pipe(map(([activeApp, activeNavId]) => this.findActiveNavigationItem(activeApp, activeNavId)));

  /**
   * BELANGRIJK — niet weghalen: hergebruik van de laatste opzoeking.
   *
   * `findActiveNavigationItem` kloont de volledige navigatieboom (lodash `cloneDeep`, ~1,7 ms).
   * Deze synchrone variant wordt via `PlatformAppContext.activeNavigationItem` gelezen vanuit de
   * layout- en breadcrumb-templates (geen OnPush), dus bij élke change-detection-ronde, in dev
   * zelfs twee keer. Zonder hergebruik kostte elke muis-event met een Angular-listener ~10 ms op de
   * chart-pagina; scrollen over het basis-paneel (klikbare kaartjes) werd daardoor stroef
   * (gemeten 2026-10-13: 11,9 → 0,5 ms per event).
   *
   * Veilig omdat de store app-objecten bij elke wijziging vervangt (geen in-place mutatie): zolang
   * dezelfde app (identiteit) en `navId` actief zijn, is het resultaat hetzelfde. Zie
   * router-active-app.queries.spec.ts (`getActiveNavigationItem`).
   */
  getActiveNavigationItem(): UserNavigationItem | undefined {
    const activeApp = this.getActiveApp();
    const activeNavId = this.leafSnapshot().queryParams[navigationItemIdQueryParam];
    const cached = this.lastActiveNavigationItem;
    if (cached && cached.app === activeApp && cached.navId === activeNavId) {
      return cached.item;
    }
    const item = this.findActiveNavigationItem(activeApp, activeNavId);
    this.lastActiveNavigationItem = { app: activeApp, navId: activeNavId, item };
    return item;
  }

  private lastActiveNavigationItem?: {
    readonly app: UserEgiApp | undefined;
    readonly navId: string | undefined;
    readonly item: UserNavigationItem | undefined;
  };

  private selectParam(name: string): Observable<string | undefined> {
    return this.committedLeaf$.pipe(
      map(leaf => leaf.params[name]),
      distinctUntilChanged()
    );
  }

  private selectQueryParam(name: string): Observable<string | undefined> {
    return this.committedLeaf$.pipe(
      map(leaf => leaf.queryParams[name]),
      distinctUntilChanged()
    );
  }

  /** Met paramsInheritanceStrategy 'always' bevat de diepste route ook de params van zijn ouders. */
  private leafSnapshot(): ActivatedRouteSnapshot {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private findActiveApp(availableApps: UserEgiApp[], appId: string): UserEgiApp | undefined {
    return availableApps?.find(a => a.id === appId);
  }

  private findActiveNavigationItem(
    app: UserEgiApp,
    activeNavId: string
  ): UserNavigationItem | undefined {
    return app?.rootNavigationItem.clone().asArray.find(i => i.id === activeNavId);
  }
}
